import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '../../hooks/useDebounce';
import { deliveryClient } from '../../lib/connect/clients';
import { useTenantId } from '../../pages/Delivery/_shared/hooks/useTenantId';
export const REGISTRY_SEARCH_DEBOUNCE_MS = 200;
export const REGISTRY_SEARCH_MIN_CHARS = 2;
export const REGISTRY_SEARCH_LIMIT = 20;
const SYSTEM_TENANT = '_system';
async function searchRegistry(kind, tenantId, term, signal) {
    switch(kind){
        case 'CONTACT':
            {
                const resp = await deliveryClient.listContacts({
                    companyId: tenantId,
                    search: term
                }, {
                    signal
                });
                return resp.contacts.map((c)=>({
                        kind,
                        registryId: c.id,
                        label: `${c.firstName} ${c.lastName}`.trim() || 'Unnamed person',
                        sublabel: [
                            c.title,
                            c.governmentOrganizationName || c.companyProfileName || c.organization,
                            c.email
                        ].filter(Boolean).join(' · ') || undefined
                    }));
            }
        case 'COMPANY':
            {
                const resp = await deliveryClient.listCompanyProfiles({
                    companyId: tenantId,
                    search: term
                }, {
                    signal
                });
                return resp.profiles.map((c)=>({
                        kind,
                        registryId: c.id,
                        label: c.name,
                        sublabel: [
                            c.isOwnCompany ? 'Your organization' : '',
                            c.uei ? `UEI ${c.uei}` : '',
                            c.cageCode ? `CAGE ${c.cageCode}` : ''
                        ].filter(Boolean).join(' · ') || undefined
                    }));
            }
        case 'GOVERNMENT_ORG':
            {
                const resp = await deliveryClient.listGovernmentOrganizations({
                    companyId: tenantId,
                    search: term,
                    includeSystem: true
                }, {
                    signal
                });
                return resp.organizations.map((o)=>({
                        kind,
                        registryId: o.id,
                        label: o.acronym ? `${o.name} (${o.acronym})` : o.name,
                        sublabel: o.displayPath || undefined,
                        system: o.companyId === SYSTEM_TENANT
                    }));
            }
        case 'VEHICLE':
            {
                const resp = await deliveryClient.listContractVehicles({
                    companyId: tenantId
                }, {
                    signal
                });
                const needle = term.toLowerCase();
                return resp.vehicles.filter((v)=>v.name.toLowerCase().includes(needle) || v.contractNumber.toLowerCase().includes(needle)).map((v)=>({
                        kind,
                        registryId: v.id,
                        label: v.name,
                        sublabel: [
                            v.vehicleType,
                            v.contractNumber
                        ].filter(Boolean).join(' · ') || undefined
                    }));
            }
    }
}
/**
 * GRW-37: the registry search behind RegistryEntityPicker. Debounced 200 ms,
 * at least 2 characters, at most 20 results. Server-filtered (the picker
 * disables the combobox's local filter), except vehicles, whose list RPC has
 * no search term and is filtered on name / contract number here.
 */ export function useRegistryEntitySearch(kind, query) {
    const tenantId = useTenantId();
    const term = useDebounce(query.trim(), REGISTRY_SEARCH_DEBOUNCE_MS);
    const enabled = !!tenantId && term.length >= REGISTRY_SEARCH_MIN_CHARS;
    const result = useQuery({
        queryKey: [
            'registry-entity-search',
            tenantId,
            kind,
            term
        ],
        enabled,
        staleTime: 30000,
        queryFn: async ({ signal })=>(await searchRegistry(kind, tenantId, term, signal)).slice(0, REGISTRY_SEARCH_LIMIT)
    });
    return {
        options: enabled ? result.data ?? [] : [],
        /** The typed query has settled into the term searched. */ settled: term === query.trim(),
        loading: enabled && result.isFetching || term !== query.trim(),
        isError: result.isError,
        refetch: result.refetch
    };
}
